import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { Search, Copy, Sparkles, Loader2, ClipboardPaste, ChevronDown } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { useLogs } from "../components/LogProvider"
import { useLocale } from "../components/LocaleProvider"
import { toast } from "../components/ui/toast"
import type { TranslationKey } from "../i18n/translations"
import { api } from "../lib/api"
import { useMails, useClients, useCreateMail, useUpdateMailContent } from "../lib/queries"
import { useAuth } from "../components/AuthProvider"
import { getUserColor } from "../lib/utils"
import type { MailTemplateRecord } from "../lib/api"
import type { Client } from "../types"

const MAIL_TYPES = ["Gratitude", "Feedback", "After Review"] as const

const TEXT_LABEL_T_KEY: Record<string, TranslationKey> = {
  Gratitude: "mail_type_gratitude",
  Feedback: "mail_type_feedback",
  "After Review": "mail_type_after_review",
}

function makeMails() {
  return MAIL_TYPES.map((type) => ({ type, german: "", ukrainian: "" }))
}

function ClientSelect({
  clients,
  value,
  onChange,
  required,
}: {
  clients: Client[]
  value: number
  onChange: (id: number) => void
  required?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const selected = clients.find((c) => c.id === value)
  const colorMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c.color || getUserColor(c.fullName)])),
    [clients],
  )

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring flex items-center justify-between"
      >
        <span className="flex items-center gap-2 truncate">
          {selected && (
            <span
              className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: colorMap[selected.id] }}
            />
          )}
          {selected?.fullName ?? "—"}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-md max-h-60 overflow-auto">
          {clients.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c.id)
                setOpen(false)
              }}
              className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-accent transition-colors text-left ${
                c.id === value ? "bg-accent" : ""
              }`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: colorMap[c.id] }}
              />
              {c.fullName}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Mails() {
  const { data: mailsData, isLoading } = useMails()
  const data: MailTemplateRecord[] = mailsData ?? []
  const { data: clientsData } = useClients()
  const clients: Client[] = clientsData ?? []
  const clientColorMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.fullName, c.color || getUserColor(c.fullName)])),
    [clients],
  )
  const createMut = useCreateMail()
  const updateMailContentMut = useUpdateMailContent()
  useAuth()
  const { logAction } = useLogs()
  const { t } = useLocale()
  const [search, setSearch] = useState("")

  const [mailModalOpen, setMailModalOpen] = useState(false)
  const [mailTemplateId, setMailTemplateId] = useState<number | null>(null)
  const [mailContentId, setMailContentId] = useState<number | null>(null)
  const [mailGerman, setMailGerman] = useState("")
  const [mailUkrainian, setMailUkrainian] = useState("")

  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [selectedMode, setSelectedMode] = useState<string>("Gratitude")
  const [selectedClientId, setSelectedClientId] = useState<number>(0)
  const [instructions, setInstructions] = useState("")
  const [aiGerman, setAiGerman] = useState("")
  const [aiUkrainian, setAiUkrainian] = useState("")
  const [aiTyping, setAiTyping] = useState(false)
  const [aiMaxLength, setAiMaxLength] = useState<number>(700)
  const [knowAgent, setKnowAgent] = useState(false)
  const [aiAgentName, setAiAgentName] = useState("")
  const [letterDate, setLetterDate] = useState("")
  const [recordModalOpen, setRecordModalOpen] = useState(false)
  const [recordClientId, setRecordClientId] = useState<number>(0)
  const [recordMailType, setRecordMailType] = useState<string>("Gratitude")
  const [recordGerman, setRecordGerman] = useState("")

  const typingTimers = useRef<ReturnType<typeof setTimeout>[]>([])

  useEffect(() => {
    if (clients.length > 0 && selectedClientId === 0) {
      setSelectedClientId(clients[0].id)
    }
  }, [clients, selectedClientId])

  useEffect(() => {
    return () => {
      typingTimers.current.forEach(clearTimeout)
      typingTimers.current = []
    }
  }, [])

  const filtered = useMemo(
    () => data.filter((row) => row.client.toLowerCase().includes(search.toLowerCase())),
    [data, search],
  )

  const openMailModal = (templateId: number, mailId: number, german: string, ukrainian: string) => {
    setMailTemplateId(templateId)
    setMailContentId(mailId)
    setMailGerman(german)
    setMailUkrainian(ukrainian)
    setMailModalOpen(true)
  }

  const saveMailContent = async () => {
    if (mailTemplateId === null || mailContentId === null) return
    const record = data.find((d) => d.id === mailTemplateId)
    const mail = record?.mails.find((m) => m.id === mailContentId)
    if (mail && (mail.german !== mailGerman || mail.ukrainian !== mailUkrainian)) {
      logAction("mails", "edit", `${record?.client} ${mail.type}: content modified`, record?.client)
    }
    await updateMailContentMut.mutateAsync({
      templateId: mailTemplateId,
      mailId: mailContentId,
      dto: { german: mailGerman, ukrainian: mailUkrainian },
    })
    setMailModalOpen(false)
    setMailTemplateId(null)
    setMailContentId(null)
  }

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text)
    toast(t("toast_copied"))
  }

  const animateText = useCallback((text: string, setter: (val: string) => void) => {
    const chars = text.split("")
    let current = ""
    let i = 0
    const timer = () => {
      if (i < chars.length) {
        current += chars[i]
        setter(current)
        i++
        const char = chars[i - 1]
        let delay = 6
        if (char === "." || char === "!" || char === "?") delay = 60
        else if (char === ",") delay = 30
        else if (char === "\n") delay = 40
        else if (char === " ") delay = 4
        const id = setTimeout(timer, delay)
        typingTimers.current.push(id)
      }
    }
    timer()
  }, [])

  const handleGenerate = async () => {
    const selectedClient = clients.find((c) => c.id === selectedClientId)
    if (!selectedClient) return
    typingTimers.current.forEach(clearTimeout)
    typingTimers.current = []
    setAiGerman("")
    setAiUkrainian("")
    setAiTyping(true)
    try {
      const result = await api.generateMail({
        type: selectedMode,
        clientId: selectedClientId,
        instructions,
        maxLength: aiMaxLength,
        agentName: knowAgent ? aiAgentName : undefined,
        letterDate: letterDate || undefined,
      })
      animateText(result.german, setAiGerman)
      animateText(result.ukrainian, setAiUkrainian)
      const maxLen = Math.max(result.german.length, result.ukrainian.length)
      const totalDelay = maxLen * 6 + 200
      setTimeout(() => {
        setAiTyping(false)
      }, totalDelay)
      logAction(
        "mails",
        "add",
        `Generated AI mail (${selectedMode}) for ${selectedClient.fullName}`,
        selectedClient.fullName,
      )
    } catch (err: any) {
      setAiTyping(false)
      toast(err?.message || "Failed to generate mail")
    }
  }

  const saveGenerated = async () => {
    if (!aiGerman && !aiUkrainian) return
    const selectedClient = clients.find((c) => c.id === selectedClientId)
    if (!selectedClient) return
    const existing = data.find((r) => r.clientId === selectedClientId)
    if (existing) {
      const mailEntry = existing.mails.find((m) => m.type === selectedMode)
      if (mailEntry?.id) {
        await updateMailContentMut.mutateAsync({
          templateId: existing.id,
          mailId: mailEntry.id,
          dto: { german: aiGerman, ukrainian: aiUkrainian },
        })
      }
    } else {
      await createMut.mutateAsync({
        clientId: selectedClientId,
        mails: MAIL_TYPES.map((type) => ({
          type,
          german: type === selectedMode ? aiGerman : "",
          ukrainian: type === selectedMode ? aiUkrainian : "",
        })),
      })
    }
    setAiModalOpen(false)
    setAiGerman("")
    setAiUkrainian("")
    toast("Mail saved")
  }

  const handleRecordSave = async () => {
    if (!recordGerman.trim() || recordClientId === 0) return
    const selectedClient = clients.find((c) => c.id === recordClientId)
    const existing = data.find((r) => r.clientId === recordClientId)
    if (existing) {
      const mailEntry = existing.mails.find((m) => m.type === recordMailType)
      if (mailEntry?.id) {
        await updateMailContentMut.mutateAsync({
          templateId: existing.id,
          mailId: mailEntry.id,
          dto: { german: recordGerman },
        })
      }
    } else {
      await createMut.mutateAsync({
        clientId: recordClientId,
        mails: MAIL_TYPES.map((type) => ({
          type,
          german: type === recordMailType ? recordGerman : "",
        })),
      })
    }
    logAction(
      "mails",
      "add",
      `Recorded ${recordMailType} mail for ${selectedClient?.fullName ?? ""}`,
      selectedClient?.fullName,
    )
    toast(t("toast_mail_recorded"))
    setRecordModalOpen(false)
    setRecordClientId(0)
    setRecordMailType("Gratitude")
    setRecordGerman("")
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_clients")}
            className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              typingTimers.current.forEach(clearTimeout)
              typingTimers.current = []
              setSelectedMode("Gratitude")
              setSelectedClientId(clients[0]?.id ?? 0)
              setInstructions("")
              setAiGerman("")
              setAiUkrainian("")
              setAiTyping(false)
              setKnowAgent(false)
              setAiAgentName("")
              setAiAgentName("")
              setLetterDate("")
              setAiModalOpen(true)
            }}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            {t("btn_create_ai_mail")}
          </button>
          <button
            onClick={() => {
              setRecordClientId(clients[0]?.id ?? 0)
              setRecordMailType("Gratitude")
              setRecordGerman("")
              setRecordModalOpen(true)
            }}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
          >
            <ClipboardPaste className="h-4 w-4" />
            {t("btn_record")}
          </button>
        </div>
      </div>

      {/* Mail Content Modal */}
      <Dialog
        open={mailModalOpen}
        onOpenChange={(v) => {
          if (!v) {
            setMailModalOpen(false)
            setMailTemplateId(null)
            setMailContentId(null)
          }
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              {mailTemplateId !== null && mailContentId !== null
                ? `${data.find((d) => d.id === mailTemplateId)?.client ?? ""} — ${t(TEXT_LABEL_T_KEY[data.find((d) => d.id === mailTemplateId)?.mails.find((m) => m.id === mailContentId)?.type ?? "Gratitude"])}`
                : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">{t("th_german")}</label>
                  <button
                    type="button"
                    onClick={() => handleCopy(mailGerman)}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    {t("btn_copy")}
                  </button>
                </div>
                <textarea
                  value={mailGerman}
                  onChange={(e) => setMailGerman(e.target.value)}
                  rows={10}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none font-sans leading-relaxed"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">{t("th_ukrainian")}</label>
                  <button
                    type="button"
                    onClick={() => handleCopy(mailUkrainian)}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    {t("btn_copy")}
                  </button>
                </div>
                <textarea
                  value={mailUkrainian}
                  onChange={(e) => setMailUkrainian(e.target.value)}
                  rows={10}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none font-sans leading-relaxed"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <DialogClose asChild>
                <button
                  type="button"
                  className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                >
                  {t("btn_cancel")}
                </button>
              </DialogClose>
              <button
                type="button"
                onClick={saveMailContent}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {t("btn_save")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Mail Modal */}
      <Dialog open={recordModalOpen} onOpenChange={setRecordModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialog_record_mail")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("form_client")}</label>
              <ClientSelect
                clients={clients}
                value={recordClientId}
                onChange={setRecordClientId}
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("form_mail_type")}</label>
              <select
                value={recordMailType}
                onChange={(e) => setRecordMailType(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {MAIL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(TEXT_LABEL_T_KEY[type])}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("th_german")}</label>
              <textarea
                value={recordGerman}
                onChange={(e) => setRecordGerman(e.target.value)}
                placeholder="Paste German text here..."
                rows={10}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none font-sans leading-relaxed"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <DialogClose asChild>
                <button
                  type="button"
                  className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                >
                  {t("btn_cancel")}
                </button>
              </DialogClose>
              <button
                type="button"
                onClick={handleRecordSave}
                disabled={!recordGerman.trim() || recordClientId === 0}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("btn_save")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Mail Generator Modal */}
      <Dialog open={aiModalOpen} onOpenChange={(v) => setAiModalOpen(v)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("dialog_ai_mail")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left side — editable text areas */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">{t("th_german")}</label>
                  <button
                    type="button"
                    onClick={() => handleCopy(aiGerman)}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    {t("btn_copy")}
                  </button>
                </div>
                <textarea
                  value={aiGerman}
                  onChange={(e) => setAiGerman(e.target.value)}
                  placeholder="German text will appear here after generation..."
                  rows={20}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none font-sans leading-relaxed"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">{t("th_ukrainian")}</label>
                  <button
                    type="button"
                    onClick={() => handleCopy(aiUkrainian)}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    {t("btn_copy")}
                  </button>
                </div>
                <textarea
                  value={aiUkrainian}
                  onChange={(e) => setAiUkrainian(e.target.value)}
                  placeholder="Ukrainian text will appear here after generation..."
                  rows={20}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none font-sans leading-relaxed"
                />
              </div>
            </div>

            {/* Right side — controls */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {MAIL_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setSelectedMode(type)
                      setAiGerman("")
                      setAiUkrainian("")
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedMode === type
                        ? "bg-primary text-primary-foreground"
                        : "border border-input bg-background hover:bg-accent"
                    }`}
                  >
                    {t(TEXT_LABEL_T_KEY[type])}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("form_client")}</label>
                <ClientSelect
                  clients={clients}
                  value={selectedClientId}
                  onChange={(id) => {
                    setSelectedClientId(id)
                    setAiGerman("")
                    setAiUkrainian("")
                  }}
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setKnowAgent(!knowAgent)
                    if (knowAgent) setAiAgentName("")
                  }}
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border text-sm transition-colors ${
                    knowAgent
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input bg-background text-muted-foreground hover:bg-accent"
                  }`}
                >
                  <span
                    className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${
                      knowAgent ? "bg-primary" : "bg-input"
                    }`}
                  >
                    <span
                      className={`h-4 w-4 rounded-full bg-background shadow transition-transform ${
                        knowAgent ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </span>
                  {t("toggle_know_agent")}
                </button>
              </div>
              {knowAgent && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("form_agent")}</label>
                  <input
                    value={aiAgentName}
                    onChange={(e) => {
                      setAiAgentName(e.target.value)
                      setAiGerman("")
                      setAiUkrainian("")
                    }}
                    placeholder={t("placeholder_agent")}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("th_date")}</label>
                <input
                  type="text"
                  value={letterDate}
                  onChange={(e) => setLetterDate(e.target.value)}
                  placeholder="20.08.2026"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("th_template")}
                </label>
                <textarea
                  readOnly
                  value={
                    selectedMode === "Gratitude"
                      ? "Thank-you letter after a property viewing. Expresses gratitude, mentions Jobcenter rent coverage, emphasizes the importance of the opportunity, and conveys hope for a positive outcome. Signed with client's name."
                      : selectedMode === "Feedback"
                        ? "Professional feedback letter after a positive interaction. Thanks for professionalism, expresses satisfaction, and reaffirms interest. Signed with client's name."
                        : "Follow-up letter after property inspection. Thanks for the opportunity, mentions positive impressions, and expresses continued interest. Signed with client's name."
                  }
                  rows={5}
                  className="w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground resize-none font-sans leading-relaxed cursor-not-allowed"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("form_instructions")}</label>
                <textarea
                  value={instructions}
                  onChange={(e) => {
                    setInstructions(e.target.value)
                    setAiGerman("")
                    setAiUkrainian("")
                  }}
                  placeholder={t("placeholder_instructions")}
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-muted-foreground">Max length:</label>
                <button
                  type="button"
                  onClick={() => setAiMaxLength(aiMaxLength === 700 ? 1000 : 700)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    aiMaxLength === 1000 ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      aiMaxLength === 1000 ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-sm font-medium tabular-nums">{aiMaxLength}</span>
              </div>

              <button
                type="button"
                onClick={handleGenerate}
                disabled={aiTyping}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiTyping ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {aiTyping ? "Generating..." : t("btn_generate")}
              </button>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <DialogClose asChild>
              <button
                type="button"
                className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
              >
                {t("btn_close")}
              </button>
            </DialogClose>
            <button
              type="button"
              onClick={saveGenerated}
              disabled={!aiGerman && !aiUkrainian}
              className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("btn_save")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("th_client")}</TableHead>
              {MAIL_TYPES.map((type) => (
                <TableHead key={type}>{t(TEXT_LABEL_T_KEY[type])}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={MAIL_TYPES.length + 1}
                  className="text-center text-muted-foreground py-8"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={MAIL_TYPES.length + 1}
                  className="text-center text-muted-foreground py-8"
                >
                  {t("empty_no_results")}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{
                          backgroundColor: clientColorMap[row.client] || getUserColor(row.client),
                        }}
                      />
                      {row.client}
                    </span>
                  </TableCell>
                  {[...row.mails]
                    // Legacy templates may still contain a Termin mail row;
                    // hide it so the table stays aligned with the current types.
                    .filter((mail) => (MAIL_TYPES as readonly string[]).includes(mail.type))
                    .sort(
                      (a, b) =>
                        MAIL_TYPES.indexOf(a.type as any) - MAIL_TYPES.indexOf(b.type as any),
                    )
                    .map((mail) => (
                      <TableCell key={mail.type}>
                        {mail.german ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <button
                              onClick={() =>
                                openMailModal(row.id, mail.id, mail.german, mail.ukrainian)
                              }
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors text-sm truncate max-w-[140px] text-left"
                              title={mail.german}
                            >
                              {mail.german.length > 40
                                ? mail.german.slice(0, 40) + "…"
                                : mail.german}
                            </button>
                            <button
                              onClick={() => handleCopy(mail.german)}
                              className="h-7 w-7 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors shrink-0"
                              title={t("btn_copy")}
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        ) : null}
                      </TableCell>
                    ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Mails
